import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { ADVISORS, getLocalizedAdvisor } from '../../data/advisors';
import { computeAdvisorInsights, getInsightTone } from '../../utils/advisorInsights';
import { Brain, X, AlertOctagon, AlertTriangle, Sparkles, Info, ChevronRight, ListChecks } from 'lucide-react';

const SEVERITY_STYLE = {
  critical:    { color: 'text-danger', border: 'border-danger/40', bg: 'bg-danger/5',  icon: AlertOctagon, labelKey: 'insight_severity_critical' },
  warning:     { color: 'text-warn',   border: 'border-warn/40',   bg: 'bg-warn/5',    icon: AlertTriangle, labelKey: 'insight_severity_warning' },
  opportunity: { color: 'text-money',  border: 'border-money/40',  bg: 'bg-money/5',   icon: Sparkles, labelKey: 'insight_severity_opportunity' },
  info:        { color: 'text-slate-300', border: 'border-ink-600', bg: 'bg-ink-700/30', icon: Info, labelKey: 'insight_severity_info' },
};

const CATEGORY_LABEL_KEYS = {
  finance:    'insight_category_finance',
  fleet:      'insight_category_fleet',
  hr:         'insight_category_hr',
  commercial: 'insight_category_commercial',
  fiscalite:  'insight_category_fiscalite',
};

/**
 * AdvisorInsightsPanel — a slide-over panel that lists the current strategic
 * recommendations. Tone of each insight adapts to the selected advisor
 * (Maurice sarcastic vs Camille kind & pedagogical).
 */
export const AdvisorInsightsPanel = ({ onClose }) => {
  const { t } = useTranslation();
  // We subscribe to the entire store snapshot — insights are recomputed on
  // each render anyway and the panel is short-lived.
  const state = useGameStore();
  const insights = useMemo(() => computeAdvisorInsights(state), [state]);
  const advisorId = state.selectedAdvisor;
  const advisor = advisorId ? getLocalizedAdvisor(advisorId) : null;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-end bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="advisor-insights-overlay"
    >
      <div
        className="bg-ink-800 w-full max-w-md h-full overflow-y-auto shadow-2xl border-l border-ink-600 animate-in slide-in-from-right-4 duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="advisor-insights-panel"
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-4 py-3 border-b border-ink-600 flex items-center gap-3"
          style={advisor ? { background: `linear-gradient(135deg, ${advisor.color} 0%, ${advisor.accentColor} 120%)` } : { background: '#0f172a' }}
        >
          {advisor ? (
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
              <img src={advisor.portrait} alt={advisor.shortName} className="w-full h-full object-cover object-top" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
              <Brain className="w-6 h-6 text-accent" />
            </div>
          )}
          <div className="flex-1 min-w-0 text-white">
            <p className="font-display font-bold text-base truncate">
              {advisor ? t('advisor_advice_from', { name: advisor.shortName }) : t('strategic_insights_label')}
            </p>
            <p className="text-[11px] text-white/70 italic">
              {advisor ? advisor.title : t('advisor_none_selected')} · {t('insight_signals_count', { count: insights.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white p-1 transition-colors"
            data-testid="advisor-insights-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Insights list */}
        <div className="p-4 space-y-3" data-testid="advisor-insights-list">
          {insights.length === 0 ? (
            <div className="text-center py-12 px-4" data-testid="advisor-insights-empty">
              <div className="w-14 h-14 rounded-full bg-money/10 flex items-center justify-center mx-auto mb-3">
                <ListChecks className="w-7 h-7 text-money" />
              </div>
              <p className="text-sm font-semibold text-slate-200">{t('advisor_all_clear_title')}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                {t('advisor_all_clear_body')}
              </p>
            </div>
          ) : (
            insights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} advisorId={advisorId} onClose={onClose} />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-ink-600 px-4 py-3 text-[10px] text-slate-500 leading-relaxed">
          <p className="flex items-start gap-1.5">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5 text-accent" />
            <span>
              {t('advisor_footer_realtime_p1')}
              {' '}
              {t('advisor_footer_realtime_p2')}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// === Insight card ===
// ============================================================================

const InsightCard = ({ insight, advisorId, onClose }) => {
  const { t } = useTranslation();
  const style = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.info;
  const Icon = style.icon;
  const tone = advisorId ? getInsightTone(insight, advisorId) : insight.body;

  return (
    <div
      className={`rounded-lg border ${style.border} ${style.bg} p-3`}
      data-testid={`insight-${insight.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${style.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`text-[10px] font-mono uppercase tracking-wider ${style.color}`}>
              {t(style.labelKey)} · {CATEGORY_LABEL_KEYS[insight.category] ? t(CATEGORY_LABEL_KEYS[insight.category]) : insight.category}
            </span>
            {insight.metric && (
              <span className="text-[10px] font-mono text-slate-400" data-testid={`insight-${insight.id}-metric`}>
                {insight.metric}
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-100 leading-snug" data-testid={`insight-${insight.id}-title`}>
            {insight.title}
          </p>
          {advisorId && tone && tone !== insight.body && (
            <p
              className="text-[11px] text-slate-300 italic mt-1.5 leading-relaxed border-l-2 pl-2 border-ink-500"
              data-testid={`insight-${insight.id}-tone`}
            >
              "{tone}"
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed" data-testid={`insight-${insight.id}-body`}>
            {insight.body}
          </p>
          {insight.actions && insight.actions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`insight-${insight.id}-actions`}>
              {insight.actions.map((a, i) => (
                <Link
                  key={`${a.to}-${a.label}`}
                  to={a.to}
                  onClick={onClose}
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border ${style.border} ${style.color} hover:bg-ink-700/40 transition-colors`}
                  data-testid={`insight-${insight.id}-action-${i}`}
                >
                  {a.label}
                  <ChevronRight className="w-3 h-3" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvisorInsightsPanel;
